import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import CTAButton from '../components/CTAButton';
import StatusBadge from '../components/StatusBadge';
import { useSeo } from '../utils/seo';
import { javaApi } from '../services/api';

const CodeBySkilloraLandingPage = () => {
  useSeo(
    'Code by Skillora - Online Java Compiler | Quantum Stack',
    'Code by Skillora - Premium online Java compiler by Quantum Stack. Write, compile, and run Java code instantly in your browser. Best Java IDE and code editor for developers. Practice Java programming with Skillora.'
  );

  const [code, setCode] = useState(`public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
    }
}`);
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');

  const handleCodeChange = (e) => {
    setCode(e.target.value);
    if (validationError) {
      setValidationError('');
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleRun = async () => {
    if (!code.trim()) {
      setValidationError('Please enter some Java code to execute.');
      return;
    }

    setValidationError('');
    setIsLoading(true);
    setOutput('Running code...');
    setStatus('');

    try {
      const response = await javaApi.executeCode(code, input);
      setOutput(response.output || 'No output');
      setStatus(response.status || 'success');
      
      if (!response.success) {
        setStatus(response.status || 'error');
      }
    } catch (error) {
      console.error('Execution failed:', error);
      
      if (error.response) {
        const { status: statusCode, data } = error.response;
        if (statusCode === 400) {
          setOutput(data.output || 'Bad request: Invalid input');
          setStatus('error');
        } else if (statusCode >= 500) {
          setOutput('Server error: Please try again later');
          setStatus('error');
        } else {
          setOutput(data.output || `Error: ${statusCode}`);
          setStatus(data.status || 'error');
        }
      } else if (error.request) {
        setOutput('Network error: Unable to connect to server');
        setStatus('error');
      } else {
        setOutput('Unexpected error: Please try again');
        setStatus('error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="skillora-landing">
        {/* Hero Section */}
        <section className="skillora-hero">
          <div className="skillora-container">
            <img src="/logo.png" alt="Code by Skillora" className="hero-logo" />
            <h1>Online Java Compiler</h1>
            <p className="skillora-subheading">
              Write, run, and test Java code directly in your browser
            </p>
            <div className="skillora-intro">
              <p>
                Code by Skillora provides a simple online Java compiler for writing and executing 
                Java code without installation. Our tool handles compilation and execution in your 
                browser, making it convenient for testing Java programs and learning programming concepts.
              </p>
            </div>
            <div className="skillora-cta">
              <CTAButton to="#code-editor">Run Code Now</CTAButton>
            </div>
          </div>
        </section>

        {/* What is an Online Java Compiler Section */}
        <section className="skillora-info-section">
          <div className="skillora-container">
            <h2>What is an Online Java Compiler</h2>
            <p>
              An online Java compiler is a web-based tool that compiles and runs Java code in your browser. 
              Code by Skillora handles the compilation process automatically, converting your Java source code 
              into executable bytecode and displaying the output. This eliminates the need for local Java 
              installation and setup.
            </p>
          </div>
        </section>

        {/* How to Run Java Code Online Section */}
        <section className="skillora-info-section">
          <div className="skillora-container">
            <h2>How to Run Java Code Online</h2>
            <p>
              Running Java code online with Code by Skillora is straightforward. Type your Java code in the 
              editor, add any input if your program requires it, and click the Run button. The system compiles 
              and executes your code, then shows the output in the results panel. No configuration or setup 
              is needed.
            </p>
          </div>
        </section>

        {/* Main Section - Code Editor */}
        <section id="code-editor" className="skillora-editor-section">
          <div className="skillora-container">
            <div className="skillora-code-executor">
              <div className="skillora-editor-panel">
                <h3>Java Code</h3>
                <div className="skillora-editor-actions">
                  {/* <button 
                    onClick={handleFormat}
                    disabled={isLoading}
                    className="skillora-format-btn"
                  >
                    Format Code
                  </button> */}
                </div>
                <textarea
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="Enter your Java code here..."
                  className={`skillora-code-textarea ${validationError ? 'error' : ''}`}
                  spellCheck={false}
                />
                {validationError && <div className="skillora-validation-error">{validationError}</div>}
              </div>

              <div className="skillora-input-panel">
                <h3>Input</h3>
                <textarea
                  value={input}
                  onChange={handleInputChange}
                  placeholder="Enter input for your program..."
                  className="skillora-input-textarea"
                  spellCheck={false}
                />
              </div>

              <div className="skillora-action-panel">
                <button
                  onClick={handleRun}
                  disabled={isLoading}
                  className="skillora-run-btn"
                >
                  {isLoading ? 'Running...' : 'Run'}
                </button>
              </div>

              <div className="skillora-output-panel">
                <div className="skillora-output-header">
                  <h3>Output</h3>
                  {status && !isLoading && <StatusBadge status={status} />}
                </div>
                <div className={`skillora-output-textarea ${status?.toLowerCase()} ${isLoading ? 'loading' : ''}`}>
                  {isLoading ? 'Running...' : output || 'Results will appear here...'}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="skillora-features">
          <div className="skillora-container">
            <h2>Features</h2>
            <div className="skillora-features-grid">
              <div className="skillora-feature">
                <h3>Run Java Code in Browser</h3>
                <p>Execute Java programs directly in your web browser without any installation.</p>
              </div>
              <div className="skillora-feature">
                <h3>Provide Custom Input</h3>
                <p>Supply input data to your Java programs through the input panel.</p>
              </div>
              <div className="skillora-feature">
                <h3>View Output Instantly</h3>
                <p>See your program's output immediately after execution with status indicators.</p>
              </div>
              <div className="skillora-feature">
                <h3>Format Code</h3>
                <p>Format your Java code automatically with proper indentation and structure.</p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="skillora-how-it-works">
          <div className="skillora-container">
            <h2>How It Works</h2>
            <div className="skillora-steps">
              <div className="skillora-step">
                <div className="skillora-step-number">1</div>
                <h3>Write Code</h3>
                <p>Type your Java code in the editor</p>
              </div>
              <div className="skillora-step">
                <div className="skillora-step-number">2</div>
                <h3>Click Run</h3>
                <p>Execute your code with the Run button</p>
              </div>
              <div className="skillora-step">
                <div className="skillora-step-number">3</div>
                <h3>View Output</h3>
                <p>See the results in the output panel</p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer Section */}
        <footer className="skillora-footer">
          <div className="skillora-container">
            <div className="skillora-footer-content">
              <div className="skillora-footer-section">
                <h3>Code by Skillora</h3>
                <p>Java code execution in your browser</p>
              </div>
              <div className="skillora-footer-section">
                <h4>Learn More</h4>
                <ul>
                  <li><Link to="/java-online-compiler">Online Java Compiler</Link></li>
                  <li><Link to="/run-java-code-online">Run Java Code Online</Link></li>
                  <li><Link to="/java-playground">Java Playground</Link></li>
                </ul>
              </div>
            </div>
            <div className="skillora-footer-bottom">
              <p>&copy; 2024 Code by Skillora. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </Layout>
  );
};

export default CodeBySkilloraLandingPage;
